import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    FaUserTie, FaUsers, FaEuroSign, FaSave, FaCheckCircle,
    FaExclamationCircle, FaExchangeAlt, FaBuilding, FaFutbol,
    FaGavel, FaIdCard, FaTshirt, FaPlus, FaTrash, FaChevronDown,
    FaChevronUp, FaPercent, FaLayerGroup, FaCopy, FaFolderOpen
} from 'react-icons/fa';
import './CostsManager.css';
import API_BASE_URL from '../../config/api';

const MONTH_NAMES = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const CostsManager = () => {
    const [costs, setCosts] = useState({
        CostCoachLevel1: '125',
        CostCoachLevel2: '175',
        CostCoachLevel3: '225',
        CostCoachSeniorBonus: '50',
        CostCoordinatorPerModality: '400',
        CostFixedPerAthlete: '16.5',
        CostOperacional_Total: '0',
    });
    const [sports, setSports] = useState([]);
    const [escalaos, setEscalaos] = useState([]);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    // Expansion states
    const [expandedSports, setExpandedSports] = useState({});
    const [expandedEscalaos, setExpandedEscalaos] = useState({});
    const [showGeneralCosts, setShowGeneralCosts] = useState({});

    // Custom labels state
    const [newCustomLabels, setNewCustomLabels] = useState({});
    const [newEscCustomLabels, setNewEscCustomLabels] = useState({});

    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        Promise.all([
            fetchCosts(),
            fetchSports(),
            fetchEscalaos(),
            fetchTeams()
        ]).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedYear, selectedMonth]);

    const fetchCosts = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/costs`, {
                headers,
                params: { year: selectedYear, month: selectedMonth }
            });
            setCosts(res.data || {});
        } catch {
            setStatus({ type: 'error', message: 'Erro ao carregar custos.' });
        }
    };

    const fetchSports = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/sports`, { headers });
            setSports((res.data || []).filter(s => s.isActive));
        } catch {
            // Silently ignore
        }
    };

    const fetchEscalaos = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/escalaos`, { headers });
            setEscalaos(res.data || []);
        } catch {
            // Silently ignore
        }
    };

    const fetchTeams = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/teams`, { headers });
            setTeams(res.data || []);
        } catch {
            // Silently ignore
        }
    };

    const handleInputChange = useCallback((key, value) => {
        setCosts(prev => ({ ...prev, [key]: value }));
        if (status.message) setStatus({ type: '', message: '' });
    }, [status.message]);

    const handleSave = async () => {
        setSaving(true);
        setStatus({ type: '', message: '' });
        try {
            // Normalise empty strings → '0' so the backend decimal.Parse never fails
            const sanitised = Object.fromEntries(
                Object.entries(costs).map(([k, v]) => [k, v === '' || v === undefined ? '0' : v])
            );
            await axios.post(`${API_BASE_URL}/api/costs`, sanitised, {
                headers,
                params: { year: selectedYear, month: selectedMonth }
            });
            setStatus({ type: 'success', message: `Custos para ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear} guardados!` });
            setTimeout(() => setStatus({ type: '', message: '' }), 3500);
        } catch {
            setStatus({ type: 'error', message: 'Erro ao guardar custos.' });
        } finally {
            setSaving(false);
        }
    };

    const handleCopyPrevious = async () => {
        const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
        const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
        try {
            const res = await axios.get(`${API_BASE_URL}/api/costs`, {
                headers,
                params: { year: prevYear, month: prevMonth }
            });
            setCosts(res.data || {});
            setStatus({ type: 'success', message: `Valores copiados de ${MONTH_NAMES[prevMonth - 1]} ${prevYear}! (Podes alterar e guardar)` });
        } catch {
            setStatus({ type: 'error', message: 'Erro ao copiar valores do mês anterior.' });
        }
    };

    // ── Escalões Mapping Helper ─────────────────────────────────────────────
    const getEscalaosForSport = useCallback((sportId) => {
        const fromEscalaoSports = (escalaos || []).filter(e =>
            e.isActive !== false && e.sportIds && e.sportIds.includes(sportId)
        );
        const fromTeams = (teams || [])
            .filter(t => t.sportId === sportId && t.escalaoId)
            .map(t => ({
                id: t.escalaoId,
                name: t.category || t.name,
                minAge: null,
                maxAge: null
            }));

        const map = new Map();
        fromEscalaoSports.forEach(e => map.set(e.id, { id: e.id, name: e.name, minAge: e.minAge, maxAge: e.maxAge }));
        fromTeams.forEach(t => {
            if (!map.has(t.id) && t.name) {
                map.set(t.id, { id: t.id, name: t.name, minAge: null, maxAge: null });
            }
        });

        return Array.from(map.values()).sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
        );
    }, [escalaos, teams]);

    // ── Custom cost helpers (Sport-level) ───────────────────────────────────
    const getCustomCosts = (sportId) => {
        const prefix = `CostDesportivo_${sportId}_Custom_`;
        return Object.entries(costs)
            .filter(([k]) => k.startsWith(prefix) && !k.includes('_Esc_'))
            .map(([k, v]) => ({
                key: k,
                slug: k.slice(prefix.length),
                label: k.slice(prefix.length).replace(/_/g, ' '),
                value: v
            }));
    };

    const addCustomCost = (sportId) => {
        const rawLabel = (newCustomLabels[sportId] || '').trim();
        if (!rawLabel) return;
        const slug = rawLabel.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
        const key = `CostDesportivo_${sportId}_Custom_${slug}`;
        setCosts(prev => ({ ...prev, [key]: '0' }));
        setNewCustomLabels(prev => ({ ...prev, [sportId]: '' }));
    };

    // ── Custom cost helpers (Escalão-level) ──────────────────────────────────
    const getEscalaoCustomCosts = (sportId, escalaoId) => {
        const prefix = `CostDesportivo_${sportId}_Esc_${escalaoId}_Custom_`;
        return Object.entries(costs)
            .filter(([k]) => k.startsWith(prefix))
            .map(([k, v]) => ({
                key: k,
                slug: k.slice(prefix.length),
                label: k.slice(prefix.length).replace(/_/g, ' '),
                value: v
            }));
    };

    const addEscalaoCustomCost = (sportId, escalaoId) => {
        const fieldKey = `${sportId}_${escalaoId}`;
        const rawLabel = (newEscCustomLabels[fieldKey] || '').trim();
        if (!rawLabel) return;
        const slug = rawLabel.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
        const key = `CostDesportivo_${sportId}_Esc_${escalaoId}_Custom_${slug}`;
        setCosts(prev => ({ ...prev, [key]: '0' }));
        setNewEscCustomLabels(prev => ({ ...prev, [fieldKey]: '' }));
    };

    const removeCustomCost = async (key) => {
        setCosts(prev => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
        try {
            await axios.delete(`${API_BASE_URL}/api/costs/key`, {
                headers,
                params: { key, year: selectedYear, month: selectedMonth }
            });
        } catch {
            // Ignore
        }
    };

    // ── Replicate costs to other escalões in the same sport ─────────────────
    const handleReplicateEscalao = (sportId, sourceEscalaoId) => {
        const sourceArb = costs[`CostDesportivo_${sportId}_Esc_${sourceEscalaoId}_Arbitragem`] ?? '';
        const sourceInsc = costs[`CostDesportivo_${sportId}_Esc_${sourceEscalaoId}_Inscricoes`] ?? '';
        const sourceEquip = costs[`CostDesportivo_${sportId}_Esc_${sourceEscalaoId}_Equipamentos`] ?? '';

        const sportEscalaos = getEscalaosForSport(sportId);
        setCosts(prev => {
            const next = { ...prev };
            sportEscalaos.forEach(esc => {
                if (esc.id !== sourceEscalaoId) {
                    if (sourceArb !== '') next[`CostDesportivo_${sportId}_Esc_${esc.id}_Arbitragem`] = sourceArb;
                    if (sourceInsc !== '') next[`CostDesportivo_${sportId}_Esc_${esc.id}_Inscricoes`] = sourceInsc;
                    if (sourceEquip !== '') next[`CostDesportivo_${sportId}_Esc_${esc.id}_Equipamentos`] = sourceEquip;
                }
            });
            return next;
        });

        setStatus({
            type: 'success',
            message: 'Valores replicados para todos os outros escalões desta modalidade! Lembra-te de Fixar Mês.'
        });
        setTimeout(() => setStatus({ type: '', message: '' }), 4000);
    };

    const toggleSport = (id) => setExpandedSports(prev => ({ ...prev, [id]: !prev[id] }));

    const toggleEscalao = (sportId, escId) => {
        const key = `${sportId}_${escId}`;
        setExpandedEscalaos(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const toggleShowGeneral = (sportId) => {
        setShowGeneralCosts(prev => ({ ...prev, [sportId]: !prev[sportId] }));
    };

    // ── Calculation Helpers ────────────────────────────────────────────────
    const getEscalaoTotal = (sportId, escId) => {
        const arb = parseFloat(costs[`CostDesportivo_${sportId}_Esc_${escId}_Arbitragem`] || 0);
        const insc = parseFloat(costs[`CostDesportivo_${sportId}_Esc_${escId}_Inscricoes`] || 0);
        const equip = parseFloat(costs[`CostDesportivo_${sportId}_Esc_${escId}_Equipamentos`] || 0);
        const customs = getEscalaoCustomCosts(sportId, escId);
        const customSum = customs.reduce((acc, c) => acc + parseFloat(c.value || 0), 0);
        return arb + insc + equip + customSum;
    };

    const getSportGeneralTotal = (sportId) => {
        const arb = parseFloat(costs[`CostDesportivo_${sportId}_Arbitragem`] || 0);
        const insc = parseFloat(costs[`CostDesportivo_${sportId}_Inscricoes`] || 0);
        const equip = parseFloat(costs[`CostDesportivo_${sportId}_Equipamentos`] || 0);
        const customCosts = getCustomCosts(sportId);
        const customSum = customCosts.reduce((acc, c) => acc + parseFloat(c.value || 0), 0);
        return arb + insc + equip + customSum;
    };

    const getSportGrandTotal = (sportId) => {
        const generalTotal = getSportGeneralTotal(sportId);
        const sportEscalaos = getEscalaosForSport(sportId);
        const escalaoTotal = sportEscalaos.reduce((acc, esc) => acc + getEscalaoTotal(sportId, esc.id), 0);
        return generalTotal + escalaoTotal;
    };

    const totalOperacionalPct = sports.reduce((sum, s) => {
        return sum + parseFloat(costs[`CostOperacional_Pct_${s.id}`] || 0);
    }, 0);

    if (loading) {
        return (
            <div className="costs-loading">
                <div className="ca-spinner"></div>
                <p>A carregar configurações de custos...</p>
            </div>
        );
    }

    return (
        <div className="costs-manager-container">
            {/* ── Header ── */}
            <div className="costs-header">
                <div className="costs-title-section">
                    <FaEuroSign className="costs-main-icon" />
                    <div>
                        <h3>Gestão de Custos por Período</h3>
                        <p>Define os custos base, operacionais e desportivos por modalidade e escalão.</p>
                    </div>
                </div>

                <div className="costs-period-selector">
                    <div className="select-wrapper">
                        <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
                            {MONTH_NAMES.map((m, i) => (
                                <option key={i + 1} value={i + 1}>{m}</option>
                            ))}
                        </select>
                    </div>
                    <div className="select-wrapper">
                        <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                            {[2024, 2025, 2026, 2027].map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                    <button className="costs-copy-btn" onClick={handleCopyPrevious} title="Copiar do mês anterior">
                        <FaExchangeAlt />
                    </button>
                    <button
                        className={`costs-save-btn ${saving ? 'saving' : ''}`}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? <div className="btn-spinner"></div> : <FaSave />}
                        Fixar Mês
                    </button>
                </div>
            </div>

            {status.message && (
                <div className={`costs-status-banner ${status.type}`}>
                    {status.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
                    {status.message}
                </div>
            )}

            <div className="costs-grid">
                {/* ── Treinadores ── */}
                <div className="costs-section">
                    <div className="costs-section-header">
                        <FaUserTie className="section-icon" />
                        <h4>Treinadores</h4>
                    </div>
                    <div className="costs-inputs">
                        <div className="cost-input-group">
                            <label>Nível 1 (€)</label>
                            <input
                                type="number"
                                value={costs.CostCoachLevel1 ?? ''}
                                onChange={(e) => handleInputChange('CostCoachLevel1', e.target.value)}
                            />
                        </div>
                        <div className="cost-input-group">
                            <label>Nível 2 (€)</label>
                            <input
                                type="number"
                                value={costs.CostCoachLevel2 ?? ''}
                                onChange={(e) => handleInputChange('CostCoachLevel2', e.target.value)}
                            />
                        </div>
                        <div className="cost-input-group">
                            <label>Nível 3 (€)</label>
                            <input
                                type="number"
                                value={costs.CostCoachLevel3 ?? ''}
                                onChange={(e) => handleInputChange('CostCoachLevel3', e.target.value)}
                            />
                        </div>
                        <div className="cost-input-group bonus-input">
                            <label>Bónus Seniores (+€)</label>
                            <input
                                type="number"
                                value={costs.CostCoachSeniorBonus ?? ''}
                                onChange={(e) => handleInputChange('CostCoachSeniorBonus', e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* ── Estrutura ── */}
                <div className="costs-section">
                    <div className="costs-section-header">
                        <FaUsers className="section-icon" />
                        <h4>Estrutura</h4>
                    </div>
                    <div className="costs-inputs">
                        <div className="cost-input-group">
                            <label>Coordenador - Valor Padrão Base (€)</label>
                            <input
                                type="number"
                                value={costs.CostCoordinatorPerModality ?? ''}
                                onChange={(e) => handleInputChange('CostCoordinatorPerModality', e.target.value)}
                            />
                        </div>

                        {sports.length > 0 && (
                            <div className="structure-sports-coordinators">
                                <span className="structure-subtitle">Coordenador por Modalidade (€)</span>
                                <div className="sport-coordinator-grid">
                                    {sports.map(sport => {
                                        const key = `CostCoordinator_${sport.id}`;
                                        const val = costs[key] ?? '';
                                        return (
                                            <div key={sport.id} className="cost-input-group sport-coord-group">
                                                <label className="sport-coord-label">{sport.name}</label>
                                                <input
                                                    type="number"
                                                    placeholder={costs.CostCoordinatorPerModality || '400'}
                                                    value={val}
                                                    onChange={(e) => handleInputChange(key, e.target.value)}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Atletas ── */}
                <div className="costs-section">
                    <div className="costs-section-header">
                        <FaEuroSign className="section-icon" />
                        <h4>Atletas</h4>
                    </div>
                    <div className="costs-inputs">
                        <div className="cost-input-group">
                            <label>Custo Fixo por Atleta / Mês (€)</label>
                            <input
                                type="number"
                                step="0.1"
                                value={costs.CostFixedPerAthlete ?? ''}
                                onChange={(e) => handleInputChange('CostFixedPerAthlete', e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* ── Custos Operacionais ── */}
                <div className="costs-section costs-section--operational">
                    <div className="costs-section-header">
                        <FaBuilding className="section-icon section-icon--orange" />
                        <h4>Custos Operacionais</h4>
                    </div>
                    <div className="costs-inputs">
                        <div className="cost-input-group">
                            <label>Total de Custos Operacionais / Mês (€)</label>
                            <input
                                type="number"
                                step="0.01"
                                value={costs.CostOperacional_Total ?? ''}
                                onChange={(e) => handleInputChange('CostOperacional_Total', e.target.value)}
                            />
                        </div>

                        {sports.length > 0 && (
                            <div className="operational-pct-section">
                                <div className="operational-pct-header">
                                    <FaPercent className="pct-icon" />
                                    <span>Distribuição por Modalidade</span>
                                    <span className={`pct-total-badge ${totalOperacionalPct > 100 ? 'over' : totalOperacionalPct === 100 ? 'exact' : ''}`}>
                                        {totalOperacionalPct.toFixed(0)}% alocado
                                    </span>
                                </div>
                                <div className="operational-pct-list">
                                    {sports.map(sport => {
                                        const key = `CostOperacional_Pct_${sport.id}`;
                                        const pct = parseFloat(costs[key] || 0);
                                        const allocated = (parseFloat(costs.CostOperacional_Total || 0) * pct / 100).toFixed(2);
                                        return (
                                            <div key={sport.id} className="pct-row">
                                                <span className="pct-sport-name">{sport.name}</span>
                                                <div className="pct-input-wrapper">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="1"
                                                        value={costs[key] ?? ''}
                                                        onChange={(e) => handleInputChange(key, e.target.value)}
                                                        className="pct-input"
                                                    />
                                                    <span className="pct-symbol">%</span>
                                                </div>
                                                <span className="pct-amount">≈ €{allocated}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Custos Desportivos por Modalidade e Escalão ── */}
            {sports.length > 0 && (
                <div className="sports-costs-section">
                    <div className="sports-costs-header">
                        <FaFutbol className="section-icon section-icon--blue" />
                        <div>
                            <h4>Custos por Modalidade e por Escalão</h4>
                            <p className="section-subtitle-text">
                                Define custos desportivos individualizados para cada escalão dentro de cada modalidade (Arbitragem, Inscrições, Equipamentos e Outros).
                            </p>
                        </div>
                    </div>

                    <div className="sports-costs-list">
                        {sports.map(sport => {
                            const isExpanded = !!expandedSports[sport.id];
                            const grandTotal = getSportGrandTotal(sport.id);
                            const generalTotal = getSportGeneralTotal(sport.id);
                            const sportEscalaos = getEscalaosForSport(sport.id);
                            const customCosts = getCustomCosts(sport.id);
                            const arbKey = `CostDesportivo_${sport.id}_Arbitragem`;
                            const inscKey = `CostDesportivo_${sport.id}_Inscricoes`;
                            const equipKey = `CostDesportivo_${sport.id}_Equipamentos`;

                            return (
                                <div key={sport.id} className={`sport-cost-card ${isExpanded ? 'expanded' : ''}`}>
                                    <button
                                        className="sport-cost-toggle"
                                        onClick={() => toggleSport(sport.id)}
                                        type="button"
                                    >
                                        <div className="sport-cost-toggle-left">
                                            <span className="sport-cost-name">{sport.name}</span>
                                            <span className="sport-escalaocount-tag">
                                                <FaLayerGroup /> {sportEscalaos.length} escal{sportEscalaos.length !== 1 ? 'ões' : 'ão'}
                                            </span>
                                        </div>

                                        <div className="sport-cost-toggle-right">
                                            {grandTotal > 0 && (
                                                <span className="sport-cost-total">Total: €{grandTotal.toFixed(2)}/mês</span>
                                            )}
                                            {isExpanded ? <FaChevronUp className="toggle-chevron" /> : <FaChevronDown className="toggle-chevron" />}
                                        </div>
                                    </button>

                                    {isExpanded && (
                                        <div className="sport-cost-body">
                                            {/* ── Sub-Section: Escalões da Modalidade ── */}
                                            <div className="escalaoscosts-header-bar">
                                                <div className="escalaoscosts-header-title">
                                                    <FaLayerGroup className="esc-title-icon" />
                                                    <div>
                                                        <h5>Escalões de {sport.name}</h5>
                                                        <span>Custos específicos de arbitragem, inscrições federativas e equipamentos de cada escalão</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {sportEscalaos.length === 0 ? (
                                                <div className="no-escalaos-warning">
                                                    <FaExclamationCircle />
                                                    <span>Nenhum escalão associado a esta modalidade no momento. Podes definir custos gerais abaixo ou criar escalões em Gestão de Escalões.</span>
                                                </div>
                                            ) : (
                                                <div className="escalaoscosts-grid">
                                                    {sportEscalaos.map(esc => {
                                                        const escExpandedKey = `${sport.id}_${esc.id}`;
                                                        const isEscExpanded = expandedEscalaos[escExpandedKey] ?? true;
                                                        const escTotal = getEscalaoTotal(sport.id, esc.id);
                                                        const escArbKey = `CostDesportivo_${sport.id}_Esc_${esc.id}_Arbitragem`;
                                                        const escInscKey = `CostDesportivo_${sport.id}_Esc_${esc.id}_Inscricoes`;
                                                        const escEquipKey = `CostDesportivo_${sport.id}_Esc_${esc.id}_Equipamentos`;
                                                        const escCustomCosts = getEscalaoCustomCosts(sport.id, esc.id);

                                                        return (
                                                            <div key={esc.id} className={`escalao-cost-box ${isEscExpanded ? 'expanded' : ''}`}>
                                                                <div
                                                                    className="escalao-cost-box-header"
                                                                    onClick={() => toggleEscalao(sport.id, esc.id)}
                                                                >
                                                                    <div className="escalao-box-title">
                                                                        <span className="escalao-name-tag">{esc.name}</span>
                                                                        {(esc.minAge || esc.maxAge) && (
                                                                            <span className="escalao-age-tag">
                                                                                {esc.minAge && esc.maxAge
                                                                                    ? `${esc.minAge}-${esc.maxAge} anos`
                                                                                    : esc.minAge
                                                                                    ? `+${esc.minAge} anos`
                                                                                    : `Até ${esc.maxAge} anos`}
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    <div className="escalao-box-actions" onClick={e => e.stopPropagation()}>
                                                                        <span className={`escalao-subtotal-badge ${escTotal > 0 ? 'has-cost' : ''}`}>
                                                                            €{escTotal.toFixed(2)}/mês
                                                                        </span>

                                                                        {sportEscalaos.length > 1 && (
                                                                            <button
                                                                                type="button"
                                                                                className="escalao-replicate-btn"
                                                                                title="Replicar valores deste escalão para todos os outros desta modalidade"
                                                                                onClick={() => handleReplicateEscalao(sport.id, esc.id)}
                                                                            >
                                                                                <FaCopy /> Replicar
                                                                            </button>
                                                                        )}

                                                                        <button
                                                                            type="button"
                                                                            className="escalao-box-chevron"
                                                                            onClick={() => toggleEscalao(sport.id, esc.id)}
                                                                            title="Expandir/Recolher escalão"
                                                                        >
                                                                            {isEscExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                {isEscExpanded && (
                                                                    <div className="escalao-cost-box-content">
                                                                        <div className="escalao-inputs-row">
                                                                            <div className="cost-input-group">
                                                                                <label>
                                                                                    <FaGavel className="field-icon orange" /> Arbitragem (€/mês)
                                                                                </label>
                                                                                <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    step="0.01"
                                                                                    placeholder="0.00"
                                                                                    value={costs[escArbKey] ?? ''}
                                                                                    onChange={(e) => handleInputChange(escArbKey, e.target.value)}
                                                                                />
                                                                            </div>

                                                                            <div className="cost-input-group">
                                                                                <label>
                                                                                    <FaIdCard className="field-icon green" /> Inscrições Federativas (€/mês)
                                                                                </label>
                                                                                <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    step="0.01"
                                                                                    placeholder="0.00"
                                                                                    value={costs[escInscKey] ?? ''}
                                                                                    onChange={(e) => handleInputChange(escInscKey, e.target.value)}
                                                                                />
                                                                            </div>

                                                                            <div className="cost-input-group">
                                                                                <label>
                                                                                    <FaTshirt className="field-icon blue" /> Equipamentos (€/mês)
                                                                                </label>
                                                                                <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    step="0.01"
                                                                                    placeholder="0.00"
                                                                                    value={costs[escEquipKey] ?? ''}
                                                                                    onChange={(e) => handleInputChange(escEquipKey, e.target.value)}
                                                                                />
                                                                            </div>
                                                                        </div>

                                                                        {/* Custom costs for this escalão */}
                                                                        {escCustomCosts.length > 0 && (
                                                                            <div className="custom-costs-list escalao-customs">
                                                                                {escCustomCosts.map(cc => (
                                                                                    <div key={cc.key} className="custom-cost-row">
                                                                                        <span className="custom-cost-label">{cc.label}</span>
                                                                                        <div className="custom-cost-input-wrapper">
                                                                                            <input
                                                                                                type="number"
                                                                                                min="0"
                                                                                                step="0.01"
                                                                                                value={cc.value}
                                                                                                onChange={(e) => handleInputChange(cc.key, e.target.value)}
                                                                                            />
                                                                                            <span className="euro-symbol">€/mês</span>
                                                                                        </div>
                                                                                        <button
                                                                                            className="remove-custom-btn"
                                                                                            onClick={() => removeCustomCost(cc.key)}
                                                                                            title="Remover custo"
                                                                                            type="button"
                                                                                        >
                                                                                            <FaTrash />
                                                                                        </button>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}

                                                                        {/* Add custom cost to this escalão */}
                                                                        <div className="add-custom-cost-row escalao-add-custom">
                                                                            <input
                                                                                type="text"
                                                                                placeholder="Custo extra para este escalão (ex: Transportes)..."
                                                                                value={newEscCustomLabels[`${sport.id}_${esc.id}`] || ''}
                                                                                onChange={(e) => setNewEscCustomLabels(prev => ({
                                                                                    ...prev,
                                                                                    [`${sport.id}_${esc.id}`]: e.target.value
                                                                                }))}
                                                                                onKeyDown={(e) => e.key === 'Enter' && addEscalaoCustomCost(sport.id, esc.id)}
                                                                                className="custom-cost-name-input"
                                                                            />
                                                                            <button
                                                                                className="add-custom-btn"
                                                                                onClick={() => addEscalaoCustomCost(sport.id, esc.id)}
                                                                                type="button"
                                                                                disabled={!(newEscCustomLabels[`${sport.id}_${esc.id}`] || '').trim()}
                                                                            >
                                                                                <FaPlus /> Adicionar
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {/* ── Sub-Section: Custos Gerais da Modalidade (Opcional) ── */}
                                            <div className="sport-general-costs-wrapper">
                                                <button
                                                    type="button"
                                                    className="sport-general-costs-toggle"
                                                    onClick={() => toggleShowGeneral(sport.id)}
                                                >
                                                    <div className="toggle-title-part">
                                                        <FaFolderOpen />
                                                        <span>Despesas Comuns / Gerais da Modalidade (não atribuíveis a um escalão único)</span>
                                                    </div>
                                                    <div className="toggle-val-part">
                                                        {generalTotal > 0 && <span className="general-total-pill">€{generalTotal.toFixed(2)}/mês</span>}
                                                        {showGeneralCosts[sport.id] ? <FaChevronUp /> : <FaChevronDown />}
                                                    </div>
                                                </button>

                                                {showGeneralCosts[sport.id] && (
                                                    <div className="sport-general-body">
                                                        <div className="sport-cost-fixed-row">
                                                            <div className="cost-input-group">
                                                                <label><FaGavel className="field-icon orange" /> Arbitragem Geral (€/mês)</label>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.01"
                                                                    placeholder="0.00"
                                                                    value={costs[arbKey] ?? ''}
                                                                    onChange={(e) => handleInputChange(arbKey, e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="cost-input-group">
                                                                <label><FaIdCard className="field-icon green" /> Inscrições Gerais (€/mês)</label>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.01"
                                                                    placeholder="0.00"
                                                                    value={costs[inscKey] ?? ''}
                                                                    onChange={(e) => handleInputChange(inscKey, e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="cost-input-group">
                                                                <label><FaTshirt className="field-icon blue" /> Equipamentos Gerais (€/mês)</label>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.01"
                                                                    placeholder="0.00"
                                                                    value={costs[equipKey] ?? ''}
                                                                    onChange={(e) => handleInputChange(equipKey, e.target.value)}
                                                                />
                                                            </div>
                                                        </div>

                                                        {/* Custom costs (Sport-level) */}
                                                        {customCosts.length > 0 && (
                                                            <div className="custom-costs-list">
                                                                {customCosts.map(cc => (
                                                                    <div key={cc.key} className="custom-cost-row">
                                                                        <span className="custom-cost-label">{cc.label}</span>
                                                                        <div className="custom-cost-input-wrapper">
                                                                            <input
                                                                                type="number"
                                                                                min="0"
                                                                                step="0.01"
                                                                                value={cc.value}
                                                                                onChange={(e) => handleInputChange(cc.key, e.target.value)}
                                                                            />
                                                                            <span className="euro-symbol">€/mês</span>
                                                                        </div>
                                                                        <button
                                                                            className="remove-custom-btn"
                                                                            onClick={() => removeCustomCost(cc.key)}
                                                                            title="Remover custo"
                                                                            type="button"
                                                                        >
                                                                            <FaTrash />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* Add custom cost (Sport-level) */}
                                                        <div className="add-custom-cost-row">
                                                            <input
                                                                type="text"
                                                                placeholder="Nome da despesa geral da modalidade..."
                                                                value={newCustomLabels[sport.id] || ''}
                                                                onChange={(e) => setNewCustomLabels(prev => ({ ...prev, [sport.id]: e.target.value }))}
                                                                onKeyDown={(e) => e.key === 'Enter' && addCustomCost(sport.id)}
                                                                className="custom-cost-name-input"
                                                            />
                                                            <button
                                                                className="add-custom-btn"
                                                                onClick={() => addCustomCost(sport.id)}
                                                                type="button"
                                                                disabled={!(newCustomLabels[sport.id] || '').trim()}
                                                            >
                                                                <FaPlus /> Adicionar
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Summary footer for this sport */}
                                            <div className="sport-total-footer">
                                                <div className="sport-total-footer-left">
                                                    <span>Subtotal Escalões: €{(grandTotal - generalTotal).toFixed(2)}/mês</span>
                                                    {generalTotal > 0 && <span>Despesas Gerais: €{generalTotal.toFixed(2)}/mês</span>}
                                                </div>
                                                <div className="sport-total-footer-right">
                                                    <strong>Total Desportivo {sport.name}: €{grandTotal.toFixed(2)}/mês</strong>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="costs-footer-info">
                <FaExclamationCircle />
                <span>Os custos são guardados por mês. Se um mês não tiver valores definidos, o sistema usa os do mês anterior.</span>
            </div>
        </div>
    );
};

export default CostsManager;
